import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/store';
import { isReportInClinic, LONG_HYPHEN } from '@/utils/constants';
import { formatUtcDate } from '@/utils/formatDate';
import {
  compareLeadChambers,
  isArchivedLead,
  isInactiveLead,
  normalizeLeadChamber
} from '@/utils/leadChambers';

import { MonitoredValuesForm } from '@/pages/physician/TransmissionReportPage/utils/types';
import { Alert } from '@/types/alert';
import { DeviceVendor } from '@/types/matchDevice';
import { Organization } from '@/types/organization';
import { ReportType, TransmissionReport } from '@/types/transmissionReport';
import { PRACTITIONER_SIGNATURE_ROLES } from '@/types/user';
import { DeviceLeadLocation, VtDeviceLead, VtLeadVendor } from '@/types/vendorTransmissionReport';

const EMPTY_VENDORS: VtLeadVendor[] = [];

import { transmissionReportApiSlice } from '../slices/api/transmissionReport/transmissionReportsApiSlice';

export const statusMap: Record<number, string> = {
  0: 'Inactive',
  1: 'Active',
  2: 'New Implant',
  3: 'Pending',
  4: 'Entered In Error'
};
export const getCurrentReport = (state: RootState, reportId?: number) => {
  if (!reportId) return null;
  const { data } =
    transmissionReportApiSlice.endpoints.getTransmissionReportDetails.select(reportId)(state);
  return data;
};

export type DeviceInfoDetails = {
  modelName: string;
  modelNumber: string;
  serialNumber: string;
  type: string;
  implantDate: string;
  vendor?: DeviceVendor;
  status?: string;
  patientId: string;
  id: string;
  isEriTracked: boolean;
  deviceTelemetry: string;
  isMriCompatibility?: boolean | null;
};

export const fetchDeviceInfoFromReport = (report: TransmissionReport): DeviceInfoDetails => {
  return {
    modelName: report.matchDevice?.modelName?.trim() || ' - ',
    modelNumber: report.matchDevice?.modelNumber?.trim() || ' - ',
    serialNumber: report.matchDevice?.serialNumber?.trim() || ' - ',
    type: report.matchDevice?.type || ' - ',
    implantDate:
      formatUtcDate(
        report.matchDevice?.implantDate ?? report?.device?.deviceImplantDate,
        'MM-DD-YYYY'
      ) || '-',
    vendor: report?.matchDevice?.vendor as DeviceVendor,
    status:
      statusMap[report?.matchDevice?.status as 0 | 1 | 2 | 3 | 4] ?? report?.matchDevice?.status,
    patientId: report?.patient?.id,
    id: report?.matchDevice?.id ?? '',
    deviceTelemetry: report.matchDevice?.deviceTelemetry || ' - ',
    isMriCompatibility: report.matchDevice?.isMriCompatibility,
    isEriTracked: report?.matchDevice?.isEriTracked ?? false
  };
};

export const selectDeviceInfo = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const values = fetchDeviceInfoFromReport(reportData.transmissionReport);
  return values;
};

export const getIsReportSecondSign = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return false;

  const { transmissionType, account } = reportData.transmissionReport || {};
  const { inClinicReportWorkflow, inClinicSingleSignRecipient, sentToEhr } = account || {};

  if (transmissionType === ReportType.InClinic || transmissionType === ReportType.RemoteInClinic) {
    if (inClinicReportWorkflow === 'firstSign' && inClinicSingleSignRecipient === 'practitioner') {
      return true;
    }
    return inClinicReportWorkflow === 'secondSign';
  }

  return sentToEhr === 'secondSign' || sentToEhr === 'pendingReview';
};

export const fetchDeviceLeadsFromReport = (
  report: TransmissionReport | undefined,
  leadVendorsList: VtLeadVendor[] | undefined
) => {
  if (!report || !leadVendorsList) return [];

  const matchDeviceLeads = [...(report.matchDevice?.leads || [])]
    // The report table shows current leads; inactive history is available in edit mode.
    .filter((lead) => !isInactiveLead(lead.leadStatus) && !isArchivedLead(lead.leadStatus))
    .sort((a, b) => compareLeadChambers(a.leadChamber, b.leadChamber));

  return matchDeviceLeads.map((lead) => {
    const manufacturer = leadVendorsList.find(
      (vendor) => vendor.leadVendorId.toString() === lead.leadManufacturer?.leadVendorId?.toString()
    )?.leadVendorName;

    return {
      location: normalizeLeadChamber(lead.leadChamber),
      vendorId: lead.leadManufacturer?.leadVendorId || '-',
      manufacturer: manufacturer || '-',
      model: lead.leadModel || '-',
      implantDate: lead.implantDate ? formatUtcDate(lead.implantDate, 'MM-DD-YYYY') || '-' : '-',
      serialNumber: lead.serialNumber || '-',
      id: lead.id || '-'
    };
  });
};

const selectReportForDeviceLeads = (state: RootState, reportId?: number) =>
  getCurrentReport(state, reportId);
const selectLeadVendors = (state: RootState) =>
  transmissionReportApiSlice.endpoints.getLeadVendorsList.select()(state).data ?? EMPTY_VENDORS;

export const selectDeviceLeads = createSelector(
  [selectReportForDeviceLeads, selectLeadVendors],
  (reportData, leadVendorsList) =>
    reportData ? fetchDeviceLeadsFromReport(reportData.transmissionReport, leadVendorsList) : null
);

export const fetchMonitoredValuesFromReport = (report: TransmissionReport): MonitoredValuesForm => {
  const { deviceLeads, deviceMeasurement } = report;
  const deviceLeadsMap = (deviceLeads ?? []).reduce(
    (acc, lead) => {
      acc[lead.leadLocation ?? ''] = lead;
      return acc;
    },
    {} as Record<string, VtDeviceLead>
  );
  const raLead = deviceLeadsMap[DeviceLeadLocation.RA];
  const rvLead = deviceLeadsMap[DeviceLeadLocation.RV];
  const lvLead = deviceLeadsMap[DeviceLeadLocation.LV];
  const extraLeads = (deviceLeads ?? []).filter((lead) => {
    const location = (lead.leadLocation ?? '').toUpperCase();
    return location && location !== 'RA' && location !== 'RV' && location !== 'LV';
  });

  const values = {
    leadIds: {
      [DeviceLeadLocation.RA]: raLead?.leadId.toString(),
      [DeviceLeadLocation.RV]: rvLead?.leadId.toString(),
      [DeviceLeadLocation.LV]: lvLead?.leadId.toString()
    },
    sensing: {
      [DeviceLeadLocation.RA]: raLead?.sensing,
      [DeviceLeadLocation.RV]: rvLead?.sensing,
      [DeviceLeadLocation.LV]: lvLead?.sensing
    },
    impedance: {
      [DeviceLeadLocation.RA]: raLead?.impedance,
      [DeviceLeadLocation.RV]: rvLead?.impedance,
      [DeviceLeadLocation.LV]: lvLead?.impedance
    },
    threshold: {
      [DeviceLeadLocation.RA]: raLead?.threshold,
      [DeviceLeadLocation.RV]: rvLead?.threshold,
      [DeviceLeadLocation.LV]: lvLead?.threshold
    },
    pulsewidth: {
      [DeviceLeadLocation.RA]: raLead?.pulsewidth,
      [DeviceLeadLocation.RV]: rvLead?.pulsewidth,
      [DeviceLeadLocation.LV]: lvLead?.pulsewidth
    },
    highVoltageImpedance: {
      [DeviceLeadLocation.RA]: LONG_HYPHEN,
      [DeviceLeadLocation.RV]: deviceMeasurement?.hvImpedance,
      [DeviceLeadLocation.LV]: LONG_HYPHEN
    },
    svcImpedance: {
      [DeviceLeadLocation.RA]: raLead?.svcImpedance,
      [DeviceLeadLocation.RV]: rvLead?.svcImpedance,
      [DeviceLeadLocation.LV]: lvLead?.svcImpedance
    },
    percentPaced: {
      [DeviceLeadLocation.RA]: deviceMeasurement?.apAPacePercent,
      [DeviceLeadLocation.RV]: deviceMeasurement?.rvpRvPacePercent,
      [DeviceLeadLocation.LV]: deviceMeasurement?.lvpPercent
    },
    bivpPercent: {
      [DeviceLeadLocation.RA]: '',
      [DeviceLeadLocation.RV]: '',
      [DeviceLeadLocation.LV]: deviceMeasurement?.bvpPercent
    },
    afBurden: {
      [DeviceLeadLocation.RA]: deviceMeasurement?.afBurden,
      [DeviceLeadLocation.RV]: LONG_HYPHEN,
      [DeviceLeadLocation.LV]: LONG_HYPHEN
    }
  };

  for (const lead of extraLeads) {
    const location = (lead.leadLocation ?? '').toUpperCase();
    values.leadIds[location] = lead.leadId.toString();
    values.sensing[location] = lead.sensing;
    values.impedance[location] = lead.impedance;
    values.threshold[location] = lead.threshold;
    values.pulsewidth[location] = lead.pulsewidth;
    values.svcImpedance[location] = lead.svcImpedance;
  }

  return values;
};

export const selectMonitoredValues = (
  state: RootState,
  reportId?: number
): MonitoredValuesForm | null => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const values = fetchMonitoredValuesFromReport(report);
  return values;
};

export const fetchBatteryStatusFromReport = (report: TransmissionReport) => {
  const device = report.device;
  return {
    voltage: device?.batteryVoltage || '',
    longevity: device?.batteryRemainingLongevity || '',
    percentRemaining: device?.batteryPercentRemaining || '',
    chargeTime: device?.capacitorChargeTime || '',
    batteryMeasurementTime: device?.batteryMeasurementTime ?? '',
    batteryStatus: device?.batteryStatus || ''
  };
};

export const fetchProgrammedParametersFromReport = (report: TransmissionReport) => {
  const programmedParameter = report.programmedParameter;
  return {
    id: programmedParameter?.id,
    mode: programmedParameter?.mode,
    lowerRate: programmedParameter?.lowerRate,
    upperRate: programmedParameter?.upperRate,
    atOrAf: programmedParameter?.atOrAf,
    vtZone1: programmedParameter?.vtZone1,
    vtZone2: programmedParameter?.vtZone2,
    pvarp: programmedParameter?.pvarp,
    pvab: programmedParameter?.pvab,
    vf: programmedParameter?.vf
  };
};

export const selectEpisodes = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const episodes = report.alerts?.filter((alert) => alert.title !== 'Presenting') || [];

  return episodes.map((episode: Alert) => {
    return {
      id: episode.id,
      title: episode.title,
      notes: episode.notes || [],
      images: episode.images || [],
      createdAt: episode.createdAt,
      updatedAt: episode.updatedAt
    };
  });
};

export const selectPresenting = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const presentingEpisode = report.alerts?.find((alert) => alert.title === 'Presenting') || null;

  return presentingEpisode;
};

export const selectDeviceBatteryStatus = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const values = fetchBatteryStatusFromReport(report);
  return values;
};

export const selectSignatureStatusAndColor = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  return {
    signatureStatus: report.signatureStatus,
    color: report.color
  };
};

export const getUnderReviewByOtherUser = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  return report?.underReviewByOtherUser ?? null;
};

export const getAreFieldsEditable = (state: RootState, checkPractitionerRole = false) => {
  if (checkPractitionerRole) {
    const userRole = state.auth.user?.role;
    const isPractitioner = userRole ? PRACTITIONER_SIGNATURE_ROLES.includes(userRole) : false;
    if (isPractitioner) return false;
  }
  const { isActiveReportSigned, isEditingSignedReport } = state.transmissionReportForm;
  if (isActiveReportSigned && !isEditingSignedReport) return false;
  return true;
};

export const getIsPhysicianInterpretationEditable = (state: RootState) => {
  const userRole = state.auth.user?.role;
  const isPractitioner = userRole ? PRACTITIONER_SIGNATURE_ROLES.includes(userRole) : false;
  const { isActiveReportSigned, isEditingSignedReport } = state.transmissionReportForm;
  if (isPractitioner && isActiveReportSigned && !isEditingSignedReport) return false;
  return true;
};

export const getIsPractitioner = (state: RootState) => {
  const userRole = state.auth.user?.role;
  return userRole ? PRACTITIONER_SIGNATURE_ROLES.includes(userRole) : false;
};

export const getIsReportInClinic = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;
  return isReportInClinic(reportData?.transmissionReport.transmissionType);
};

export const getIsRemoteReport = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  return reportData?.transmissionReport.transmissionType === 'remote';
};

export const getIsILRDevice = (state: RootState, reportId?: number) => {
  if (!reportId) return false;

  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  return reportData?.transmissionReport.device?.deviceType === 'ILR';
};

export const getSigningProvider = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  return reportData?.transmissionReport.signingProvider;
};

export const selectExcludedReports = (state: RootState, reportId?: number): string[] => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return [];

  const report = reportData.transmissionReport;
  return report.overrides?.reportsToExclude || [];
};

export const selectIsReportExcluded = (
  state: RootState,
  reportId?: number,
  reportKey?: string
): boolean => {
  if (!reportKey) return false;

  const excludedReports = selectExcludedReports(state, reportId);
  return excludedReports.includes(reportKey);
};

export const selectReportsOverrides = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  return report.overrides || null;
};

export const selectInClinicSignRestrictions = (
  state: RootState,
  reportId?: number,
  clinicOrganization?: Organization
) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) {
    return {
      isInClinicOrRemoteInClinic: false,
      hasFirstSignWorkflow: false,
      inClinicSingleSignRecipient: null,
      isRestricted: false,
      signingProviderSet: false
    };
  }

  const report = reportData.transmissionReport;

  const parentOrganization = clinicOrganization?.parent;

  const inClinicReportWorkflow =
    parentOrganization?.inClinicReportWorkflow ?? clinicOrganization?.inClinicReportWorkflow;
  const inClinicSingleSignRecipient =
    parentOrganization?.inClinicSingleSignRecipient ??
    clinicOrganization?.inClinicSingleSignRecipient;

  const isInClinicOrRemoteInClinic = [ReportType.InClinic, ReportType.RemoteInClinic].includes(
    report?.transmissionType as ReportType
  );
  const hasFirstSignWorkflow = inClinicReportWorkflow === 'firstSign';

  const isRestricted = !!(
    isInClinicOrRemoteInClinic &&
    hasFirstSignWorkflow &&
    inClinicSingleSignRecipient
  );

  return {
    isInClinicOrRemoteInClinic,
    hasFirstSignWorkflow,
    inClinicSingleSignRecipient,
    isRestricted,
    signingProviderSet: !!report.signingProvider
  };
};
